import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type { StaticDatabase } from "@-/dev.improve.haniverse.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import {
  getHskLevelLabel,
  getHskLevelsForVersion,
  getHskWordsByLevelFromDb,
  type HskLevelKey,
  type HskVersionKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import { eq, sql } from "@-/com.npmjs.drizzle-orm";
import runes from "@-/com.npmjs.runes2";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import type { CharacterRow, CharactersGradeSection } from "#characters-grade-hsk-page-entities";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import {
  type MorphemeCorpusFilter,
  morphemeCorpusFilterFromCacheKey,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";

const CJK_CHARACTER_REGEX = /[　-鿿豈-﫿]/;

/**
 * Characters introduced at `level` in learning order, excluding characters that already appeared in
 * a lower level. `seen` carries the running set of characters from every lower level so each
 * character is attributed to the first (lowest) level that introduces it — the same grouping the
 * Levels tab uses, but bucketed by HSK band instead of lesson.
 */
function collectLevelCharacters(words: readonly string[], seen: Set<string>): readonly string[] {
  const ordered: string[] = [];
  for (const word of words) {
    for (const ch of runes(word)) {
      if (!CJK_CHARACTER_REGEX.test(ch)) {
        continue;
      }
      if (seen.has(ch)) {
        continue;
      }
      seen.add(ch);
      ordered.push(ch);
    }
  }
  return ordered;
}

async function getHskSections(
  staticDb: StaticDatabase,
  hskVersion: HskVersionKey,
  availableLevels: readonly HskLevelKey[],
  corpusFilter: MorphemeCorpusFilter,
): Promise<readonly CharactersGradeSection[]> {
  const db = staticDb(import.meta.location);
  const wordsByLevel = await getHskWordsByLevelFromDb(db, hskVersion, availableLevels);
  const seen = new Set<string>();
  // Attribute each character to its first band BEFORE applying the browsing corpus, so hiding a
  // character never shifts another one into a different band.
  const sections = availableLevels.map((level) => ({
    glyphs: collectLevelCharacters(wordsByLevel.get(level) ?? [], seen).filter((glyph) =>
      corpusFilter.isGraphemeInCorpus(glyph),
    ),
    key: `hsk-${level}`,
    label: `HSK ${getHskLevelLabel(level)}`,
  }));
  const glyphs = sections.flatMap((section) => section.glyphs);
  const rows = hasLength(glyphs, 0)
    ? []
    : await db
        .select({
          glyph: staticSchema.grapheme.glyph,
          rank: sql<number | null>`MIN(${staticSchema.graphemeSubtlex.rank})`.as("min_rank"),
          strokeCount: staticSchema.grapheme.strokeCount,
        })
        .from(staticSchema.grapheme)
        .leftJoin(
          staticSchema.graphemeSubtlex,
          eq(staticSchema.graphemeSubtlex.graphemeId, staticSchema.grapheme.id),
        )
        // One JSON parameter keeps this grouped read below D1's 100-variable cap without dozens
        // of 64-character DataLoader requests. The all-band catalog needs only two round trips.
        .where(
          sql`${staticSchema.grapheme.glyph} IN (SELECT value FROM json_each(${JSON.stringify(glyphs)}))`,
        )
        .groupBy(staticSchema.grapheme.id);
  const byGlyph = new Map<string, CharacterRow>(rows.map((row) => [row.glyph, row]));
  return sections.map(({ glyphs: sectionGlyphs, key, label }) => ({
    key,
    label,
    rows: sectionGlyphs.map(
      (glyph): CharacterRow => byGlyph.get(glyph) ?? { glyph, rank: null, strokeCount: null },
    ),
  }));
}

/**
 * Query options for the Characters → HSK sections (every HSK band's characters, sectioned by band).
 * Shared by `CharactersGradeHskViewSuspense` (via `usePrefetchedSuspenseQuery`) and the HSK page
 * navigator (which warms this before navigating). The sections are derived entirely from the
 * immutable static DB (keyed only by `hskVersion` / `availableLevels` and the user's browsing
 * corpus), so no change-version. The corpus travels as its serializable `cacheKey` — the
 * predicates themselves would be dropped by query-key hashing.
 */
function useGetCharactersGradeHskPageQueryKey() {
  const { hskVersion } = useActorUser();
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const availableLevels = getHskLevelsForVersion(hskVersion);
  const { cacheKey: corpusFilterCacheKey } = useMorphemeCorpusFilter();
  return () =>
    [
      "characters-grade-hsk-view",
      "sections",
      hskVersion,
      availableLevels,
      corpusFilterCacheKey,
      { staticDb },
    ] as const;
}

const [useCharactersGradeHskPageQueryOptions, charactersGradeHskPageQueryFn] =
  createUseQueryOptions(
    useGetCharactersGradeHskPageQueryKey,
    ({
      queryKey: [_, _sections, hskVersion, availableLevels, corpusFilterCacheKey, { staticDb }],
    }) => {
      return getHskSections(
        staticDb,
        hskVersion,
        availableLevels,
        morphemeCorpusFilterFromCacheKey(corpusFilterCacheKey),
      );
    },
  );

export { useCharactersGradeHskPageQueryOptions, charactersGradeHskPageQueryFn };
